import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import AuthLayout from "./AuthLayout";
import { useAuth } from "../context/AuthContext";
import { User, Briefcase } from "lucide-react";
import { useT, localizeError } from "../components/home/i18n";

export default function Register() {
  const { register } = useAuth();
  const { t, lang } = useT();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "candidate" });
  const [agree, setAgree] = useState(false);
  const [loading, setLoading] = useState(false);
  const c = t.auth.register;

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!agree) { toast.error(c.mustAccept); return; }
    setLoading(true);
    try {
      await register({ ...form, lang, consent: { terms: true, privacy: true, guidelines: true } });
      toast.success(c.created);
      navigate(`/verify-email?email=${encodeURIComponent(form.email)}`);
    } catch (err) {
      toast.error(localizeError(err.response?.data?.detail, t));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout testid="register-page" title={c.title} subtitle={c.subtitle}>
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-2 gap-2">
          {[["candidate", c.candidate, User], ["employer", c.employer, Briefcase]].map(([val, label, Icon]) => (
            <button type="button" key={val} data-testid={`role-${val}`} onClick={() => setForm({ ...form, role: val })}
              className={`flex flex-col items-center gap-1 rounded-md border p-3 text-xs font-medium transition-colors ${form.role === val ? "border-[#1F4A38] bg-[#1F4A38]/6 text-[#1F4A38]" : "border-[#E8E4DB] text-[#5C5855]"}`}>
              <Icon className="h-5 w-5" /> {label}
            </button>
          ))}
        </div>
        <div>
          <label className="text-xs font-medium text-[#5C5855]">{c.fullName}</label>
          <input data-testid="register-name" value={form.name} onChange={upd("name")} required
            className="mt-1 w-full rounded-sm border border-[#E8E4DB] bg-white px-3 py-2.5 text-sm outline-none focus:border-[#1F4A38] focus:ring-1 focus:ring-[#1F4A38]" />
        </div>
        <div>
          <label className="text-xs font-medium text-[#5C5855]">{c.email}</label>
          <input data-testid="register-email" type="email" value={form.email} onChange={upd("email")} required
            className="mt-1 w-full rounded-sm border border-[#E8E4DB] bg-white px-3 py-2.5 text-sm outline-none focus:border-[#1F4A38] focus:ring-1 focus:ring-[#1F4A38]" />
        </div>
        <div>
          <label className="text-xs font-medium text-[#5C5855]">{c.password}</label>
          <input data-testid="register-password" type="password" value={form.password} onChange={upd("password")} required minLength={8}
            placeholder={c.passwordPlaceholder}
            className="mt-1 w-full rounded-sm border border-[#E8E4DB] bg-white px-3 py-2.5 text-sm outline-none focus:border-[#1F4A38] focus:ring-1 focus:ring-[#1F4A38]" />
        </div>
        <label className="flex items-start gap-2 text-xs text-[#5C5855]">
          <input data-testid="register-consent" type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-0.5 h-4 w-4 accent-[#1F4A38]" />
          <span>{c.consentPre}
            <Link to="/terms" target="_blank" className="font-semibold text-[#1F4A38] underline">{c.terms}</Link>{c.between1}
            <Link to="/privacy" target="_blank" className="font-semibold text-[#1F4A38] underline">{c.privacy}</Link>{c.between2}
            <Link to="/guidelines" target="_blank" className="font-semibold text-[#1F4A38] underline">{c.guidelines}</Link>{c.consentPost}
          </span>
        </label>
        <button data-testid="register-submit" disabled={loading || !agree}
          className="w-full rounded-full bg-[#1F4A38] py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60">
          {loading ? c.submitting : c.submit}
        </button>
        <p className="text-center text-sm text-[#5C5855]">
          {c.haveAccount} <Link to="/login" data-testid="go-login" className="font-semibold text-[#1F4A38]">{c.signin}</Link>
        </p>
      </form>
    </AuthLayout>
  );
}
